import { getAccessToken, logout } from './auth';

export interface VendorRef {
  id:          number;
  description: string;
}

export interface DeviceTypeRef {
  id:          number;
  description: string;
}

export interface MasterArticle {
  id:                      number;
  description:             string;
  vendor:                  VendorRef;
  hs2019DeviceType:        DeviceTypeRef;
  is_available_for_repair: boolean;
  is_hidden:               boolean;
}

export interface WarrantyServicepartner {
  id:          number;
  description: string;
}

export interface MasterArticleDetail {
  id:                      number;
  description:             string;
  warranty_servicepartner: WarrantyServicepartner | null;
  warranty_in_months:      number | null;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
  ) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api/masterarticles`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(
      body.reason ?? `Server-Fehler ${resp.status}`,
      resp.status,
      body.result,
      body.fields,
    );
  }

  return body;
}

/** `includeHidden` liefert auch ausgeblendete Artikel mit (Hakerl in der Liste). */
export async function list(includeHidden = false): Promise<MasterArticle[]> {
  const url  = includeHidden ? `${BASE}?include_hidden=1` : BASE;
  const body = await request<MasterArticle>(url, { method: 'GET' });
  return body.items ?? [];
}

export interface NewMaster {
  id:          number;
  description: string;
  vendor:      VendorRef;
}

export async function listNew(): Promise<NewMaster[]> {
  const body = await request<NewMaster>(`${BASE}/new`, { method: 'GET' });
  return body.items ?? [];
}

export async function acknowledgeAllNewMasters(): Promise<number> {
  const body = await request<{ acknowledged_count: number }>(
    `${BASE}/acknowledge-all`,
    { method: 'POST' },
  );
  return body.item?.acknowledged_count ?? 0;
}

export async function get(id: number): Promise<MasterArticleDetail> {
  const body = await request<MasterArticleDetail>(`${BASE}/${id}`, { method: 'GET' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function setAvailability(id: number, available: boolean): Promise<void> {
  await request<{ device_id: number; is_available_for_repair: boolean }>(
    `${BASE}/${id}/availability`,
    {
      method: 'PATCH',
      body:   JSON.stringify({ available }),
    },
  );
}

export async function hide(id: number): Promise<void> {
  await request<{ device_id: number; is_hidden: boolean }>(
    `${BASE}/${id}/hide`,
    { method: 'POST' },
  );
}

/** Blendet wieder ein — die Reparatur-Freigabe bleibt aus, die schaltet man danach selbst. */
export async function unhide(id: number): Promise<void> {
  await request<{ device_id: number; is_hidden: boolean }>(
    `${BASE}/${id}/unhide`,
    { method: 'POST' },
  );
}

export async function setWarrantyServicepartner(
  id: number,
  servicepartnerLocationId: number | null,
): Promise<WarrantyServicepartner | null> {
  const body = await request<{ device_id: number; warranty_servicepartner: WarrantyServicepartner | null }>(
    `${BASE}/${id}/warranty-servicepartner`,
    {
      method: 'PUT',
      body:   JSON.stringify({ servicepartner_location_id: servicepartnerLocationId }),
    },
  );
  return body.item?.warranty_servicepartner ?? null;
}

export async function setWarrantyMonths(
  id: number,
  months: number | null,
): Promise<number | null> {
  const body = await request<{ device_id: number; warranty_in_months: number | null }>(
    `${BASE}/${id}/warranty-months`,
    {
      method: 'PUT',
      body:   JSON.stringify({ months }),
    },
  );
  return body.item?.warranty_in_months ?? null;
}
